<template>
  <section ref="poll" class="war-room-post-poll" data-test="war-room-post-poll">
    <div class="war-room-post-poll__header">
      <h3 class="war-room-post-poll__title" data-test="war-room-post-poll__title">{{ poll.title }}</h3>
      <div class="war-room-post-poll__hint">Attached poll</div>
    </div>
    <RadioButtonGroup v-if="voteable" :config="radioGroupChoices" group="pollChoices" @change="onVote" />
    <WarRoomPostPollResults v-if="!voteable" :choices="poll.choices" />

    <div v-if="showHint" class="war-room-post-poll__comm-hint">*Voting in this poll no longer yields commission.</div>
  </section>
</template>

<script lang="ts">
import Vue from 'vue';
import type { PropOptions } from 'vue';
import type { IPostPoll } from '@/types/community';
import RadioButtonGroup from '@/components/common/RadioButtonGroup/RadioButtonGroup.vue';
import WarRoomPostPollResults from '@/components/partials/war-room/WarRoomPostPoll/WarRoomPostPollResults.vue';
import { sortByField } from '~/components/utils/sorting_func';
import { scrollToHTMLElement } from '~/components/utils/browser/ui';

export default Vue.extend({
  name: 'WarRoomPostPoll',
  components: {
    RadioButtonGroup,
    WarRoomPostPollResults,
  },
  props: {
    poll: {
      type: Object,
      required: true,
    } as PropOptions<IPostPoll>,
    isAuthor: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  data(): { timeoutId: number } {
    return {
      timeoutId: -1,
    };
  },
  computed: {
    radioGroupChoices(): Array<{ label: string; value: string; position: number }> {
      return this.poll.choices
        .map(({ id, text, position }) => ({
          label: text,
          value: id.toString(),
          position,
        }))
        .sort(sortByField('position'));
    },
    voteable(): boolean {
      if (this.isAuthor) return false;

      return !this.poll.choices.some(({ current_vote: currentVote }) => !!currentVote);
    },
    showHint(): boolean {
      return this.voteable && this.poll.expired_for_commission;
    },
  },
  mounted(): void {
    const pollRef = this.$refs.poll;
    if (this.$route.query.poll === 'true' && pollRef instanceof HTMLElement) {
      this.timeoutId = window.setTimeout(() => {
        scrollToHTMLElement(pollRef);
      }, 500);
    }
  },
  destroyed() {
    clearTimeout(this.timeoutId);
  },
  methods: {
    onVote(choice: number) {
      this.$emit('vote', Number(choice));
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-post-poll {
  padding: 16px 24px 24px;
  background: var(--theme-color-over-card);
  border-radius: 8px;
  position: relative;

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__title {
    color: var(--theme-color-text);

    @include headline-semibold;

    @include mq('sm') {
      @include body-semibold;
    }
  }

  &__hint {
    display: none;

    @include mq('sm') {
      display: inline-block;

      @include subhead-semibold;

      flex-shrink: 0;
      margin-left: 16px;
      color: var(--gray-50);
    }
  }

  &__comm-hint {
    @include caption2;

    margin-top: 16px;
    color: var(--gray-50);
  }
}
</style>
